import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  Box,
  Skeleton
} from '@mui/material';

import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import tracker from '../../api/tracker';
import useNotification from '../../hooks/useNotificaction';

export default function StripeAdmin() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [setConf] = useNotification();
  
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const [status, setStatus] = useState({
    configured: false,
    charges_enabled: false,
    details_submitted: false
  });

  const fetchStatus = async () => {
    try {
      const response = await tracker.get('admin/stripe/status');
      setStatus(response.data);
    } catch (error) {
      setConf({ msg: 'Error al obtener el estado de Stripe', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam === 'return') {
      setConf({ msg: 'Has regresado del registro de Stripe. Verificando estado actualizado...', variant: 'success' });
      searchParams.delete('status');
      setSearchParams(searchParams);
    } else if (statusParam === 'refresh') {
      setConf({ msg: 'La sesión de Stripe ha expirado o fue interrumpida. Por favor, intenta iniciar la configuración de nuevo.', variant: 'warning' });
      searchParams.delete('status');
      setSearchParams(searchParams);
    }
    
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnboard = async () => {
    setOnboarding(true);
    try {
      const response = await tracker.post('admin/stripe/onboard');
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setConf({ msg: 'Respuesta inválida del servidor', variant: 'error' });
        setOnboarding(false);
      }
    } catch (error) {
      setConf({ msg: 'Error al iniciar el proceso de vinculación', variant: 'error' });
      setOnboarding(false);
    }
  };

  return (
    <Page title="Vincular Stripe Connect">
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Configuración de Stripe Connect
          </Typography>
        </Stack>

        <Card sx={{ p: 5, textAlign: 'center', boxShadow: 3 }}>
          {loading ? (
            <Stack spacing={2} alignItems="center">
              <Skeleton variant="circular" width={80} height={80} />
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="text" width="40%" height={30} />
              <Skeleton variant="rectangular" width={200} height={48} sx={{ mt: 3, borderRadius: 1 }} />
            </Stack>
          ) : (
            <Box>
              <Box mb={4} display="flex" justifyContent="center">
                {status.configured && status.charges_enabled ? (
                  <Iconify icon="logos:stripe" width={80} height={80} />
                ) : (
                  <Iconify icon="mdi:bank-off-outline" width={80} height={80} sx={{ color: 'text.disabled' }} />
                )}
              </Box>

              {!status.configured && (
                <>
                  <Typography variant="h5" paragraph fontWeight="bold">
                    Vinculación Pendiente
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', mb: 4 }}>
                    La cuenta de Stripe para Holy Spirit aún no está vinculada. Haz clic en el botón para iniciar la configuración y poder recibir donaciones.
                  </Typography>
                  <Button 
                    variant="contained" 
                    size="large" 
                    onClick={handleOnboard}
                    disabled={onboarding}
                    startIcon={<Iconify icon="mdi:link-variant" />}
                  >
                    {onboarding ? 'Redirigiendo...' : 'Vincular cuenta de Stripe'}
                  </Button>
                </>
              )}

              {status.configured && (!status.charges_enabled || !status.details_submitted) && (
                <>
                  <Typography variant="h5" paragraph color="warning.main" fontWeight="bold">
                    Información Incompleta
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', mb: 4 }}>
                    Tu cuenta de Stripe requiere información adicional para poder procesar pagos.
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="warning"
                    size="large" 
                    onClick={handleOnboard}
                    disabled={onboarding}
                    startIcon={<Iconify icon="mdi:alert-circle-outline" />}
                  >
                    {onboarding ? 'Redirigiendo...' : 'Completar información'}
                  </Button>
                </>
              )}

              {status.configured && status.charges_enabled && status.details_submitted && (
                <>
                  <Typography variant="h5" paragraph color="success.main" fontWeight="bold">
                    Cuenta Vinculada y Activa
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>
                    ¡Tu cuenta de Stripe está correctamente vinculada y lista para recibir donaciones!
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Card>
      </Container>
    </Page>
  );
}
